import React, { useEffect, useRef, useState } from "react";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Dropdown } from "primereact/dropdown";
import { Checkbox } from "primereact/checkbox";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { useTranslation } from "react-i18next";
import { submitInquiry } from "../../../services/contactCenterService";
import { getCurrentUserFromLocalStorage } from "../../../helpers/localStorageHelper";
import {
  CONTACT_US_CATEGORY_OPTIONS,
  CONTACT_US_TYPE_OPTIONS,
} from "../../../constants/contact-us";
import {
  SuccessTemplate,
  ErrorTemplate,
} from "../../common/ToastMessage/Template";

const EMPTY = {
  name: "",
  email: "",
  category: null,
  type: null,
  subject: "",
  message: "",
  consent: false,
};

// English fallbacks for this form's common_ns keys. The backend common_ns source
// (Google Sheet -> ContentTranslation) overrides these once the keys are seeded; ko/vi
// are served from there. Keeps the form self-sufficient without a local bundle.
const DEFAULTS = {
  "CONTACT_US_CENTER.FORM.REQUIRED": "This field is required.",
  "CONTACT_US_CENTER.FORM.EMAIL_INVALID": "Please enter a valid email address.",
  "CONTACT_US_CENTER.FORM.CONSENT_REQUIRED": "Please agree to the consent.",
  "CONTACT_US_CENTER.FORM.SUCCESS_TITLE": "Submitted",
  "CONTACT_US_CENTER.FORM.ERROR_TITLE": "Submission failed",
  "CONTACT_US_CENTER.FORM.ERROR_DETAIL": "Something went wrong. Please try again later.",
  "CONTACT_US_CENTER.INQUIRY.NAME_LABEL": "Name",
  "CONTACT_US_CENTER.INQUIRY.NAME_PLACEHOLDER": "Enter your name.",
  "CONTACT_US_CENTER.INQUIRY.EMAIL_LABEL": "Email",
  "CONTACT_US_CENTER.INQUIRY.EMAIL_PLACEHOLDER": "Enter the email to receive replies.",
  "CONTACT_US_CENTER.INQUIRY.CATEGORY_LABEL": "Inquiry type",
  "CONTACT_US_CENTER.INQUIRY.CATEGORY_PLACEHOLDER": "Select an inquiry type.",
  "CONTACT_US_CENTER.INQUIRY.WRITER_TYPE_LABEL": "Writer Type",
  "CONTACT_US_CENTER.INQUIRY.WRITER_TYPE_PLACEHOLDER": "Select a writer type.",
  "CONTACT_US_CENTER.INQUIRY.SUBJECT_LABEL": "Subject",
  "CONTACT_US_CENTER.INQUIRY.SUBJECT_PLACEHOLDER": "Enter a subject.",
  "CONTACT_US_CENTER.INQUIRY.MESSAGE_LABEL": "Message",
  "CONTACT_US_CENTER.INQUIRY.MESSAGE_PLACEHOLDER":
    "Leave us the details and our coordinator will review and send thorough feedback to the email you provided.",
  "CONTACT_US_CENTER.INQUIRY.CONSENT":
    "I agree to the collection and overseas transfer of my personal information for the global service. (Required)",
  "CONTACT_US_CENTER.INQUIRY.SUBMIT": "Submit inquiry & get email reply",
  "CONTACT_US_CENTER.INQUIRY.SUCCESS_DETAIL":
    "Your inquiry has been received. We'll reply to the email you provided.",
};

// Basic email shape check (mirrors the LeaveMessagePopup form rule).
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Design-system field styling (matches the global LeaveMessagePopup form).
const INPUT_CLASS =
  "keduall-form input-default-default keduall-typography text-16-normal-400 text-default-txt";
const TEXTAREA_CLASS =
  "keduall-form textarea-default keduall-typography text-16-normal-400 text-default-txt p-3";

// Required-field marker rendered next to a label.
const RequiredMark = () => <span className="cuc-required">*</span>;

/** 1:1 문의 — inquiry form. Posts to ContactUs (ContactUsCreateModel). */
const InquiryTab = () => {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  // Tracks which fields the user has edited, so onBlur validation only fires once dirty.
  const [dirty, setDirty] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const toast = useRef(null);
  const { t: translate } = useTranslation("common_ns", { useSuspense: false });
  const t = (key, options) =>
    translate(key, { defaultValue: DEFAULTS[key] ?? key, ...options });

  // Prefill name & email for a logged-in user (still editable).
  useEffect(() => {
    const currentUser = getCurrentUserFromLocalStorage();
    if (currentUser) {
      setForm((f) => ({
        ...f,
        name: currentUser.displayName ?? "",
        email: currentUser.email ?? "",
      }));
    }
  }, []);

  // Inquiry categories & writer types come from the shared constants (forwarded to
  // ContactUs as Category / Type); labels are resolved through i18n.
  const categories = CONTACT_US_CATEGORY_OPTIONS.map((c) => ({
    code: c.code,
    name: t(c.labelKey),
  }));
  const writerTypes = CONTACT_US_TYPE_OPTIONS.map((c) => ({
    code: c.code,
    name: t(c.labelKey),
  }));

  const set = (key) => (e) => {
    const value = e.target ? e.target.value : e.value;
    setForm((f) => ({ ...f, [key]: value }));
    setDirty((d) => (d[key] ? d : { ...d, [key]: true }));
    // Clear a field error as soon as the user edits it.
    setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  };

  // Validate a single text field's rule. Used onBlur (only once the field is dirty)
  // for immediate feedback, mirroring the full-form validate() rules.
  const fieldError = (key, value) => {
    const v = (value ?? "").trim();
    if (key === "name" || key === "subject") {
      return v ? undefined : t("CONTACT_US_CENTER.FORM.REQUIRED");
    }
    if (key === "email") {
      if (!v) return t("CONTACT_US_CENTER.FORM.REQUIRED");
      return EMAIL_REGEX.test(v)
        ? undefined
        : t("CONTACT_US_CENTER.FORM.EMAIL_INVALID");
    }
    return undefined;
  };

  const blur = (key) => () => {
    if (!dirty[key]) return;
    setErrors((prev) => ({ ...prev, [key]: fieldError(key, form[key]) }));
  };

  // Per-field validation (required + email format). Returns the error map.
  const validate = () => {
    const next = {};
    if (!form.name?.trim()) next.name = t("CONTACT_US_CENTER.FORM.REQUIRED");
    if (!form.email?.trim()) next.email = t("CONTACT_US_CENTER.FORM.REQUIRED");
    else if (!EMAIL_REGEX.test(form.email.trim()))
      next.email = t("CONTACT_US_CENTER.FORM.EMAIL_INVALID");
    if (!form.category) next.category = t("CONTACT_US_CENTER.FORM.REQUIRED");
    if (!form.type) next.type = t("CONTACT_US_CENTER.FORM.REQUIRED");
    if (!form.subject?.trim())
      next.subject = t("CONTACT_US_CENTER.FORM.REQUIRED");
    if (!form.consent)
      next.consent = t("CONTACT_US_CENTER.FORM.CONSENT_REQUIRED");
    return next;
  };

  const showSuccess = () =>
    toast.current?.show({
      severity: "success",
      summary: t("CONTACT_US_CENTER.FORM.SUCCESS_TITLE"),
      detail: t("CONTACT_US_CENTER.INQUIRY.SUCCESS_DETAIL"),
      life: 4000,
      className: "keduall-toast",
      content: (props) => SuccessTemplate(props),
    });

  const showError = () =>
    toast.current?.show({
      severity: "error",
      summary: t("CONTACT_US_CENTER.FORM.ERROR_TITLE"),
      detail: t("CONTACT_US_CENTER.FORM.ERROR_DETAIL"),
      life: 4000,
      className: "keduall-toast",
      content: (props) => ErrorTemplate(props),
    });

  const onSubmit = async () => {
    const validationErrors = validate();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    setSubmitting(true);
    try {
      const resp = await submitInquiry(form);
      if (resp?.id) {
        setForm(EMPTY);
        setErrors({});
        setDirty({});
        showSuccess();
      } else {
        showError();
      }
    } catch {
      showError();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="cuc-form">
      <Toast ref={toast} position="bottom-right" className="keduall-toast" />
      <div className="cuc-form-grid">
        <div className="cuc-field">
          <label>
            {t("CONTACT_US_CENTER.INQUIRY.NAME_LABEL")}
            <RequiredMark />
          </label>
          <InputText
            className={`${INPUT_CLASS}${errors.name ? " p-invalid" : ""}`}
            value={form.name}
            onChange={set("name")}
            onBlur={blur("name")}
            placeholder={t("CONTACT_US_CENTER.INQUIRY.NAME_PLACEHOLDER")}
          />
          {errors.name && <small className="cuc-field-error">{errors.name}</small>}
        </div>
        <div className="cuc-field">
          <label>
            {t("CONTACT_US_CENTER.INQUIRY.EMAIL_LABEL")}
            <RequiredMark />
          </label>
          <InputText
            className={`${INPUT_CLASS}${errors.email ? " p-invalid" : ""}`}
            value={form.email}
            onChange={set("email")}
            onBlur={blur("email")}
            placeholder={t("CONTACT_US_CENTER.INQUIRY.EMAIL_PLACEHOLDER")}
          />
          {errors.email && (
            <small className="cuc-field-error">{errors.email}</small>
          )}
        </div>
      </div>

      <div className="cuc-field">
        <label>
          {t("CONTACT_US_CENTER.INQUIRY.CATEGORY_LABEL")}
          <RequiredMark />
        </label>
        <Dropdown
          className={`${INPUT_CLASS}${errors.category ? " p-invalid" : ""}`}
          value={form.category}
          options={categories}
          optionLabel="name"
          optionValue="code"
          onChange={set("category")}
          placeholder={t("CONTACT_US_CENTER.INQUIRY.CATEGORY_PLACEHOLDER")}
        />
        {errors.category && (
          <small className="cuc-field-error">{errors.category}</small>
        )}
      </div>

      <div className="cuc-field">
        <label>
          {t("CONTACT_US_CENTER.INQUIRY.WRITER_TYPE_LABEL")}
          <RequiredMark />
        </label>
        <Dropdown
          className={`${INPUT_CLASS}${errors.type ? " p-invalid" : ""}`}
          value={form.type}
          options={writerTypes}
          optionLabel="name"
          optionValue="code"
          onChange={set("type")}
          placeholder={t("CONTACT_US_CENTER.INQUIRY.WRITER_TYPE_PLACEHOLDER")}
        />
        {errors.type && (
          <small className="cuc-field-error">{errors.type}</small>
        )}
      </div>

      <div className="cuc-field">
        <label>
          {t("CONTACT_US_CENTER.INQUIRY.SUBJECT_LABEL")}
          <RequiredMark />
        </label>
        <InputText
          className={`${INPUT_CLASS}${errors.subject ? " p-invalid" : ""}`}
          value={form.subject}
          onChange={set("subject")}
          onBlur={blur("subject")}
          placeholder={t("CONTACT_US_CENTER.INQUIRY.SUBJECT_PLACEHOLDER")}
        />
        {errors.subject && (
          <small className="cuc-field-error">{errors.subject}</small>
        )}
      </div>

      <div className="cuc-field">
        <label>{t("CONTACT_US_CENTER.INQUIRY.MESSAGE_LABEL")}</label>
        <InputTextarea
          className={TEXTAREA_CLASS}
          rows={6}
          value={form.message}
          onChange={set("message")}
          placeholder={t("CONTACT_US_CENTER.INQUIRY.MESSAGE_PLACEHOLDER")}
        />
      </div>

      <div className="cuc-field">
        <label className={`cuc-consent${errors.consent ? " cuc-consent-error" : ""}`}>
          <Checkbox
            checked={form.consent}
            onChange={(e) => {
              setForm((f) => ({ ...f, consent: e.checked }));
              setErrors((prev) =>
                prev.consent ? { ...prev, consent: undefined } : prev
              );
            }}
          />
          {t("CONTACT_US_CENTER.INQUIRY.CONSENT")}
        </label>
        {errors.consent && (
          <small className="cuc-field-error">{errors.consent}</small>
        )}
      </div>

      <Button
        className="cuc-form-submit keduall-buttons button-filled-default-normal-primary text-white-txt keduall-typography heading-h6-semibold-600"
        icon="pi pi-envelope"
        label={t("CONTACT_US_CENTER.INQUIRY.SUBMIT")}
        loading={submitting}
        disabled={!form.consent}
        onClick={onSubmit}
      />
    </div>
  );
};

export default InquiryTab;
